import { FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useAddLeasingPlanMutation } from '@telialt/core-eshop/src/hooks/mutations/useAddLeasingPlanMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { LeasingPlan } from '@telialt/models/src/EshopModel';
import { selectLeasingPlanCode } from '@telialt/utils/src/eshopSelectors';
import { formatNumber, withParams } from '@telialt/utils/src/helpers';

import { LEASING_SELECTOR_RADIO_GROUP_NAME } from './constants';
import {
    LabelDiscountPrice,
    LabelDiscountTotalPrice,
    LabelMonthlyPrice,
    LeasingRadioOptionWrapper,
    Radio,
    TooltipWithRestrictionMessage,
} from './LeasingPlanSelector.styles';
import { useLeasingRestriction } from './useLeasingRestriction';

type Props = { leasingPlan: LeasingPlan; order?: number };

const restrictionInfoTooltipID = 'restriction-info-tooltip';

export const RadioLeasingOption: FC<Props> = ({ leasingPlan, order }) => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const { addLeasingPlan } = useAddLeasingPlanMutation();

    const selectedLeasingPlanCode = selectLeasingPlanCode(cart);
    const handleLeasingPlanChange = (code: string) => {
        addLeasingPlan(code);
    };
    const restriction = useLeasingRestriction();
    const hasRestriction = !!restriction && restriction.type !== 'isB2BUserWhoNeedsOnlyLeasing';

    return (
        <LeasingRadioOptionWrapper style={{ order }}>
            {hasRestriction && (
                <TooltipWithRestrictionMessage id={restrictionInfoTooltipID} content={restriction.message}>
                    {null}
                </TooltipWithRestrictionMessage>
            )}
            <Radio
                checked={leasingPlan.code === selectedLeasingPlanCode}
                onChange={() => handleLeasingPlanChange(leasingPlan.code)}
                disabled={hasRestriction}
                name={LEASING_SELECTOR_RADIO_GROUP_NAME}
                value={leasingPlan.code}
                label={withParams(t['eshop.product_page.leasing_selector.leasing_option.monthly_payment'], {
                    months: leasingPlan.durationInMonths,
                })}
            />
            <LabelDiscountPrice>
                {leasingPlan.productOfferDiscount?.value > 0 &&
                    withParams(t['eshop.product_page.leasing_selector.discount'], {
                        discount: leasingPlan.productOfferDiscount.formattedValue,
                    })}
            </LabelDiscountPrice>
            <LabelMonthlyPrice
                aria-label={`${formatNumber(leasingPlan.monthlyPrice.value)} ${t['eshop.product_page.monthly_price_unit_accessible']}`}
            >
                <span aria-hidden={true}>{leasingPlan.monthlyPrice.formattedValue}</span>
            </LabelMonthlyPrice>
            <LabelDiscountTotalPrice>
                {withParams(t['eshop.product_page.leasing_selector.leasing_option.total_price'], {
                    price: leasingPlan.totalPriceWithDiscount?.formattedValue,
                    oldPrice: null,
                })}
            </LabelDiscountTotalPrice>
        </LeasingRadioOptionWrapper>
    );
};
